/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState, useContext } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  mediaQueries,
} from '@everlywell/leaves';
import { UserContext } from '../../../components/Layout';
import { CTA, ProductsBySlugType } from 'utils/types';
import { useMembershipProducts } from 'utils/hooks';
import { addToCart, addToCartDataLayer } from 'utils/cartHelpers';

import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { reviewData } from '../../Homepage/PopularTestsSection/reviewData';
import * as S from './styles';
import useMediaQuery from 'utils/useMediaQuery';
import Img, { FluidObject } from 'gatsby-image';
import { Spinner } from 'styles/utils';
import loadable from '@loadable/component';
import { AllProductPromosContext } from '../../../templates/pdp';
const SectionWrapper = loadable(() => import('components/SectionWrapper'), {
  fallback: <Spinner />,
});

declare global {
  interface Window {
    dataLayer: any;
  }
}

type ProductCardType = {
  heading: string;
  subheading: string;
  image: FluidObject;
  number: number;
  slug: string;
  showRating: boolean;
};

export interface MoreTestsSectionProps {
  headline: string;
  mainCta: CTA;
  listOfContent: ProductCardType[];
  productsBySlug: ProductsBySlugType;
}

export const MoreTestsSection: React.FC<MoreTestsSectionProps> = ({
  headline,
  mainCta,
  listOfContent,
  productsBySlug,
}) => {
  const [itemsInCart, setItemsInCart] = useState<Array<string>>([]);
  const tabletUp = useMediaQuery(mediaQueries.forTabletHorizontalUp);
  const userData = useContext(UserContext);
  const allProductPromoData = useContext(AllProductPromosContext);

  const productsWithMembership = useMembershipProducts(
    userData,
    productsBySlug,
  );

  const handleClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.PDP_MORE_TESTS_CTA,
      },
    });
  };

  const addToCartGTM = (slug: string) => {
    if (!window || !window.dataLayer) {
      return;
    }

    const productData = productsWithMembership[slug] || {};

    if (
      !productData ||
      !productData.productId ||
      !productData.name ||
      !productData.price
    )
      return;

    addToCartDataLayer(
      // @ts-ignore
      productData.productId,
      productData.name,
      productData.price,
    );
  };

  const handleAddToCartClick = (slug: string) => {
    setItemsInCart([...itemsInCart, slug]);

    // Remove 'added' UI state
    setTimeout(() => setItemsInCart(itemsInCart.filter(x => x !== slug)), 1200);

    const product = productsWithMembership[slug];
    addToCart(product.variantId);
    addToCartGTM(slug);

    ewAnalytics.track({
      data: {
        label: ANALYTICS.LABELS.ADD_TO_CART_PDP_PRODUCT_CARD,
        'Test ID': product.productId,
        'Test Name': product.name,
      },
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
    });
  };

  const handleCardClick = (slug: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.PDP_POPULAR_PRODUCT_CARD,
        slug,
      },
    });
  };

  return (
    <SectionWrapper data-testid="popularTests">
      <S.InnerContent>
        <Container>
          <Row>
            <S.MobileHeadlineCol xs={12}>
              <S.Headline>{headline}</S.Headline>
            </S.MobileHeadlineCol>
            <S.MobileCardsCol xs={12}>
              <S.ContentContainer>
                <S.DesktopHeadline>{headline}</S.DesktopHeadline>
                <S.DesktopShopCTAButton
                  appearance="secondary"
                  href={mainCta.url}
                  onClick={handleClick}
                >
                  {mainCta.text}
                </S.DesktopShopCTAButton>
              </S.ContentContainer>
              <S.DesktopCardsContainer>
                {listOfContent.map((item, i) => {
                  const productPromoData = allProductPromoData.find(
                    page => page.slug === item.slug,
                  );
                  return (
                    <S.CardWrapper
                      key={`desktop-card-${i}`}
                      onClick={() => handleCardClick(item.slug)}
                    >
                      <Card.Product
                        image={
                          tabletUp ? (
                            <Img fluid={item.image} alt={item.heading} />
                          ) : (
                            <Img fluid={item.image} alt={item.heading} />
                          )
                        }
                        imageAlt={`${item.heading} box image`}
                        productName={item.heading}
                        productUrl={
                          productsWithMembership[item.slug] &&
                          `/products/${productsWithMembership[item.slug].slug}/`
                        }
                        price={item.subheading}
                        isOutOfStock={false} // We don't have this data yet, so we set it to false
                        reviewData={
                          item.showRating ? reviewData[item.number] : null
                        }
                        isOnSale={productPromoData?.onSale}
                        // @ts-ignore
                        promoPrice={
                          productsWithMembership[item.slug]?.memberPrice
                        }
                        promoText={
                          productsWithMembership[item.slug]?.promoText ||
                          productPromoData?.promoText
                        }
                        showAddToCart={
                          !productsWithMembership[item.slug]?.activeCredits
                        }
                        onAddToCartClick={() => handleAddToCartClick(item.slug)}
                        addedToCart={itemsInCart.includes(item.slug)}
                        onRemoveFromCartClick={() => null}
                        removedFromCart={!itemsInCart.includes(item.slug)}
                      />
                    </S.CardWrapper>
                  );
                })}
              </S.DesktopCardsContainer>
            </S.MobileCardsCol>
          </Row>
        </Container>
        <S.MobileShopCTAContainer>
          <Row center="xs">
            <Col xs={12}>
              <Button
                appearance="secondary"
                href={mainCta.url}
                onClick={handleClick}
              >
                {mainCta.text}
              </Button>
            </Col>
          </Row>
        </S.MobileShopCTAContainer>
      </S.InnerContent>
    </SectionWrapper>
  );
};

export default MoreTestsSection;
